import React, { useState, useEffect } from 'react';
import { generateWallet, updateWallet } from '../services/api';

const DEFAULT_MIN_BTC = 0.00001;
function defaultMaxBtcFromMultiplier(mult) {
  const m = parseFloat(mult);
  if (!m || m < 1) return '';
  return (0.1 / Math.round(m)).toFixed(8).replace(/\.?0+$/, '') || '0.05';
}

function WalletModal({ wallet, onClose }) {
  const isEdit = !!wallet;
  const [formData, setFormData] = useState(() => {
    const mult = wallet?.multiplier ?? '';
    const defaultMin = String(DEFAULT_MIN_BTC);
    const defaultMax = mult ? defaultMaxBtcFromMultiplier(mult) : '';
    return {
      multiplier: mult,
      address: wallet?.address || '',
      newAddress: '',
      privateKey: '',
      chance: wallet?.chance || '',
      label: wallet?.label || '',
      isActive: wallet?.is_active ?? true,
      minBetBtc: isEdit ? (wallet?.min_bet_btc ?? defaultMin) : (defaultMin),
      maxBetBtc: isEdit ? (wallet?.max_bet_btc ?? defaultMax) : defaultMax
    };
  });
  const [loading, setLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // When multiplier changes in create mode, set default max if max is empty
  useEffect(() => {
    if (isEdit) return;
    const mult = formData.multiplier;
    if (!mult || formData.maxBetBtc !== '') return;
    setFormData(prev => ({ ...prev, maxBetBtc: defaultMaxBtcFromMultiplier(mult) }));
  }, [formData.multiplier, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!isEdit && (!formData.multiplier || parseFloat(formData.multiplier) < 1.0)) {
      alert('Please enter a valid multiplier (minimum 1.0)');
      return;
    }

    try {
      setLoading(true);

      // Validate bet ranges if both provided
      if (formData.minBetBtc && formData.maxBetBtc) {
        const minBet = parseFloat(formData.minBetBtc);
        const maxBet = parseFloat(formData.maxBetBtc);
        if (minBet >= maxBet) {
          alert('❌ Min bet must be less than max bet');
          return;
        }
      }

      // Validate required fields for new wallets
      if (!isEdit) {
        if (!formData.address || !formData.address.trim()) {
          alert('❌ Please enter a wallet address');
          return;
        }
        if (!formData.privateKey || !formData.privateKey.trim()) {
          alert('❌ Please enter a private key');
          return;
        }
      }

      if (isEdit) {
        // Update existing wallet
        const updateData = {
          multiplier: parseFloat(formData.multiplier),
          chance: parseFloat(formData.chance),
          label: formData.label || null,
          is_active: formData.isActive
        };
        
        // Check if address changed (in edit mode, use newAddress field)
        const currentAddress = (wallet.address || '').trim();
        const newAddress = isEdit ? formData.newAddress.trim() : formData.address.trim();
        const addressChanged = isEdit ? (newAddress && newAddress !== currentAddress) : true; // For create mode, always use address
        
        // Add address and private key if address changed
        if (addressChanged) {
          // Validate private key is provided when address changes
          if (!formData.privateKey || !formData.privateKey.trim()) {
            alert('❌ Private key is required when updating address');
            return;
          }
          // Address changed - require private key
          updateData.address = newAddress;
          updateData.private_key = formData.privateKey.trim();
          console.log('[WalletModal] Updating address:', { currentAddress, newAddress, hasPrivateKey: !!updateData.private_key });
        }
        // Note: We don't allow updating private key without changing address,
        // as the backend requires both address and private_key together for security
        
        // Add bet ranges in BTC if provided
        if (formData.minBetBtc) {
          updateData.min_bet_btc = parseFloat(formData.minBetBtc);
        } else {
          updateData.min_bet_btc = null; // Clear if empty
        }
        if (formData.maxBetBtc) {
          updateData.max_bet_btc = parseFloat(formData.maxBetBtc);
        } else {
          updateData.max_bet_btc = null; // Clear if empty
        }
        
        await updateWallet(wallet.wallet_id, updateData);
        alert('✅ Wallet updated successfully!');
      } else {
        // Create new wallet
        const walletData = {
          multiplier: parseFloat(formData.multiplier),
          address: formData.address.trim(),
          private_key: formData.privateKey.trim(),
          chance: parseFloat(formData.chance)
        };
        
        // Add bet ranges in BTC if provided
        if (formData.minBetBtc) {
          walletData.min_bet_btc = parseFloat(formData.minBetBtc);
        }
        if (formData.maxBetBtc) {
          walletData.max_bet_btc = parseFloat(formData.maxBetBtc);
        }
        
        try {
          await generateWallet(walletData);
          alert(`✅ Successfully created ${formData.multiplier}x wallet!`);
        } catch (error) {
          const errorMsg = error.response?.data?.detail || error.message;
          // Check if it's a duplicate address error
          if (errorMsg.includes('Address already exists')) {
            setDuplicateWarning({
              message: errorMsg,
              address: formData.address.trim(),
              onConfirm: async () => {
                setDuplicateWarning(null);
                // User can proceed to edit the form or cancel
              }
            });
            return;
          }
          throw error;
        }
      }

      onClose(true); // Close modal and trigger refresh
    } catch (error) {
      alert(`❌ Failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {/* Duplicate Address Warning Modal */}
      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 rounded-lg shadow-xl max-w-md w-full p-6 border border-zinc-700">
            <h3 className="text-xl font-bold text-red-400 mb-4">⚠️ Duplicate Address Detected</h3>
            <p className="text-gray-300 mb-4">{duplicateWarning.message}</p>
            <div className="bg-orange-900/30 border border-orange-600 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-200">
                <strong>Address:</strong> <span className="font-mono text-xs">{duplicateWarning.address}</span>
              </p>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              This address is already associated with another wallet. Please use a different address or update the existing wallet.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDuplicateWarning(null)} className="flex-1 bg-zinc-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-zinc-600 font-medium">Cancel</button>
              <button onClick={() => setDuplicateWarning(null)} className="flex-1 bg-orange-500 text-black px-4 py-2 rounded-lg hover:bg-orange-400 font-medium">OK</button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col border border-zinc-700">
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 rounded-t-lg flex-shrink-0">
          <h3 className="text-xl font-bold">{isEdit ? '✏️ Edit Wallet' : '➕ Create New Wallet'}</h3>
          <p className="text-sm text-orange-100 mt-1">
            {isEdit ? 'Update wallet properties' : 'Create a new Bitcoin wallet with manual address and private key'}
          </p>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Multiplier *</label>
            <input type="number" step="0.01" min="1.0" max="1000.0" value={formData.multiplier} onChange={(e) => handleChange('multiplier', e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none" placeholder="e.g., 1.05, 1.33, 2.75" required disabled={isEdit} />
            <p className="text-xs text-gray-500 mt-1">The bet multiplier for this wallet</p>
          </div>

          <div className="border-t border-zinc-700 pt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Wallet Address {!isEdit && '*'}</label>
            {isEdit ? (
              <div>
                <div className="w-full border border-zinc-600 rounded-lg px-4 py-2 bg-zinc-800 font-mono text-sm mb-2 text-gray-300">{formData.address || 'N/A'}</div>
                <input type="text" value={formData.newAddress} onChange={(e) => handleChange('newAddress', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none font-mono text-sm"
                  placeholder="Enter new address (requires private key below)" />
                <p className="text-xs text-gray-500 mt-1">Enter a new address to update (requires private key).</p>
              </div>
            ) : (
              <>
                <input type="text" value={formData.address} onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none font-mono text-sm"
                  placeholder="e.g., bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh" required />
                <p className="text-xs text-gray-500 mt-1">Bitcoin wallet address. Must match the private key below.</p>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Private Key (WIF) {!isEdit && '*'}</label>
            <input type="password" value={formData.privateKey} onChange={(e) => handleChange('privateKey', e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none font-mono text-sm"
              placeholder={isEdit ? "Enter only if updating address" : "e.g., L5EZftvr..."} required={!isEdit} />
            <p className="text-xs text-gray-500 mt-1">
              {isEdit ? <><span className="font-semibold text-orange-400">⚠️ Required only when updating address.</span> WIF format.</> : 'Private key in WIF format. Will be encrypted.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Win Chance (%) *</label>
            <input type="number" min="0.01" max="99.99" step="0.01" value={formData.chance} onChange={(e) => handleChange('chance', e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none" placeholder="49.5" required />
            <p className="text-xs text-gray-500 mt-1">Bet wins if random number &lt; chance</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Label (Optional)</label>
            <input type="text" value={formData.label} onChange={(e) => handleChange('label', e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none" placeholder="e.g., High roller wallet" />
          </div>

          <div className="border-t border-zinc-700 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">Bet Amount Range</h4>
            <p className="text-xs text-gray-500 mb-3">Default min: 0.00001 BTC. Default max: 0.1 ÷ round(multiplier) BTC.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Min Bet (BTC)</label>
                <input type="number" step="0.00000001" min="0.00000001" value={formData.minBetBtc} onChange={(e) => handleChange('minBetBtc', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none" placeholder="0.00001" />
                <p className="text-xs text-gray-500 mt-1">Default 0.00001 BTC</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Max Bet (BTC)</label>
                <input type="number" step="0.00000001" min="0.00000001" value={formData.maxBetBtc} onChange={(e) => handleChange('maxBetBtc', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-2 focus:border-orange-500 focus:outline-none"
                  placeholder={formData.multiplier ? defaultMaxBtcFromMultiplier(formData.multiplier) : '0.1 ÷ multiplier'} />
                <p className="text-xs text-gray-500 mt-1">Default 0.1 ÷ round(multiplier)</p>
              </div>
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center">
              <input type="checkbox" id="isActive" checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)}
                className="w-4 h-4 text-orange-500 border-zinc-600 rounded focus:ring-orange-500 bg-zinc-800" />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-300">Wallet is active (accepts new bets)</label>
            </div>
          )}

          </div>

          <div className="flex gap-3 pt-4 px-6 pb-6 border-t border-zinc-700 flex-shrink-0">
            <button type="button" onClick={() => onClose(false)} disabled={loading}
              className="flex-1 bg-zinc-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-zinc-600 disabled:opacity-50 font-medium">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-orange-500 text-black px-4 py-2 rounded-lg hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium">
              {loading ? '⏳ Processing...' : isEdit ? '💾 Save Changes' : '🔑 Create Wallet'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}

export default WalletModal;
